import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, Outlet } from 'react-router-dom';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import {
  Squares2X2Icon,
  MagnifyingGlassIcon,
  DocumentArrowUpIcon,
  ListBulletIcon,
  CodeBracketSquareIcon,
  ChartBarIcon,
  CreditCardIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  XMarkIcon,
  BoltIcon,
  BellIcon,
  CheckCircleIcon as CheckCircleOutline,
  ExclamationCircleIcon,
  ArrowDownTrayIcon,
  UserIcon,
  QuestionMarkCircleIcon,
  ChatBubbleLeftIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import logo from '../../../assets/logo.png';
import { downloadBlob } from '../../../lib/downloadBlob';
import { apiClient } from '../../../lib/axios';
import { useAuthStore } from '../../../store/useAuthStore';
import { useLogout } from '../../../features/auth/api/auth';
import PlansModal from '../../../features/dashboard/PlansModal';
import { useNotifications } from '../../../features/dashboard/useNotifications';

const NAV = [
  { name: 'Dashboard',      path: '/dashboard/',               icon: Squares2X2Icon },
  { name: 'Search',         path: '/dashboard/search',         icon: MagnifyingGlassIcon },
  { name: 'Bulk Upload',    path: '/dashboard/bulk-upload',    icon: DocumentArrowUpIcon },
  { name: 'Search History', path: '/dashboard/search_history', icon: ListBulletIcon },
  { name: 'Billing',        path: '/dashboard/billing',        icon: CreditCardIcon },
  { name: 'API Access',     path: '/dashboard/api-access',     icon: CodeBracketSquareIcon },
  { name: 'API Analytics',  path: '/dashboard/api-analytics',  icon: ChartBarIcon },
  { name: 'Support',        path: '/dashboard/support',        icon: ChatBubbleLeftIcon },
];

function isActive(navPath: string, currentPath: string) {
  if (navPath === '/dashboard/') return currentPath === '/dashboard/' || currentPath === '/dashboard';
  return currentPath === navPath || currentPath.startsWith(navPath + '/');
}

function getInitials(name?: string) {
  if (!name) return 'U';
  return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
}

function timeAgo(date: Date): string {
  const secs = Math.floor((Date.now() - date.getTime()) / 1000);
  if (secs < 60) return 'just now';
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  return date.toLocaleDateString();
}

export default function DashboardLayout() {
  const location = useLocation();
  const { user, setUser } = useAuthStore();
  const { mutate: logout } = useLogout();
  const [isPlansOpen, setIsPlansOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const notifRef = useRef<HTMLDivElement>(null);

  const { notifications, unreadCount, markAllRead } = useNotifications(user?.id);

  // Close notification dropdown when clicking outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) {
        setNotifOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const openNotif = () => {
    setNotifOpen(prev => !prev);
    if (!notifOpen && unreadCount > 0) markAllRead();
  };

  const handleExportDownload = async (taskId: string) => {
    try {
      const ts = new Date().toISOString().slice(0, 19).replace(/[-T:]/g, (c) => (c === 'T' ? '_' : c));
      await downloadBlob(`/search/export/download/${taskId}`, `export_${ts}.csv`);
    } catch {
      // Export file may not be ready yet — notification was premature
    }
  };

  return (
    <div className="flex h-screen bg-slate-50 font-sans overflow-hidden">

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/30 z-30 md:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed md:relative inset-y-0 left-0 z-40 md:z-auto
        w-[260px] bg-white border-r border-slate-100 flex flex-col shrink-0
        transform transition-transform duration-200 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
      `}>

        {/* Logo */}
        <div className="h-[72px] flex items-center px-6 border-b border-slate-100 shrink-0">
          <img src={logo} alt="Localcontact" className="h-10 w-auto object-contain" />
          <button className="ml-auto md:hidden text-slate-400 hover:text-slate-700" onClick={() => setSidebarOpen(false)}>
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 px-2 py-4 space-y-0.5 overflow-y-auto">
          <p className="text-[9px] font-black text-slate-500 uppercase tracking-wider px-3 mb-3">Navigation</p>
          {NAV.map(item => {
            const active = isActive(item.path, location.pathname);
            return (
              <Link
                key={item.name}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`group flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13px] font-semibold transition-all duration-150 ${
                  active
                    ? 'bg-brand-500 text-slate-900 font-bold'
                    : 'text-slate-500 hover:bg-slate-50 hover:text-slate-900'
                }`}
              >
                <item.icon className={`w-[17px] h-[17px] shrink-0 transition-colors duration-150 ${
                  active ? 'text-slate-900' : 'text-slate-400 group-hover:text-slate-700'
                }`} />
                {item.name}
              </Link>
            );
          })}
        </nav>

        {/* Credits upsell */}
        <div className="p-4 shrink-0 border-t border-slate-100">
          <div className="bg-brand-700 p-4 rounded-2xl">
            <p className="text-[9px] font-black text-brand-200 uppercase tracking-wider mb-1">Credits</p>
            <p className="text-white text-sm font-bold mb-3">Need more firepower?</p>
            <button
              onClick={() => setIsPlansOpen(true)}
              className="w-full bg-brand-500 text-slate-900 py-2 text-[12px] font-black uppercase tracking-wide hover:bg-brand-600 transition-colors rounded-xl"
            >
              Get Credits
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col h-screen overflow-hidden min-w-0">

        {/* Header */}
        <header className="h-[72px] bg-white border-b border-slate-100 flex items-center px-6 shrink-0">
          {/* Mobile hamburger */}
          <button className="md:hidden text-slate-500 hover:text-slate-800 mr-3" onClick={() => setSidebarOpen(true)}>
            <Bars3Icon className="w-6 h-6" />
          </button>

          {/* Right-side group */}
          <div className="ml-auto flex items-center gap-3">

          {/* Credits pill */}
          <div className="flex items-center gap-2 bg-brand-500 px-4 py-2 rounded-xl">
            <BoltIcon className="w-3.5 h-3.5 text-slate-900" />
            <span className="text-[12px] font-extrabold text-slate-900">
              {(user?.credits ?? 0).toLocaleString()} Credits
            </span>
          </div>

          <div className="h-6 w-px bg-slate-100 hidden sm:block" />

          {/* Notification bell */}
          <div ref={notifRef} className="relative">
            <button
              onClick={openNotif}
              className="relative w-9 h-9 flex items-center justify-center rounded-xl hover:bg-slate-100 transition-colors text-slate-500 hover:text-slate-800"
            >
              <BellIcon className="w-5 h-5" />
              {unreadCount > 0 && (
                <span className="absolute top-1 right-1 w-4 h-4 bg-red-500 text-white text-[9px] font-extrabold rounded-full flex items-center justify-center leading-none">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>

            {notifOpen && (
              <div className="absolute right-0 top-12 w-[340px] bg-white rounded-2xl shadow-2xl border border-slate-100 z-50 overflow-hidden">
                <div className="px-4 py-3.5 border-b border-slate-100 flex items-center justify-between">
                  <span className="text-sm font-extrabold text-slate-900">Notifications</span>
                  {notifications.length > 0 && (
                    <button onClick={markAllRead} className="text-[11px] font-bold text-brand-600 hover:text-brand-800">
                      Mark all read
                    </button>
                  )}
                </div>

                <div className="max-h-[360px] overflow-y-auto divide-y divide-slate-50">
                  {notifications.length === 0 ? (
                    <div className="px-4 py-10 text-center">
                      <BellIcon className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                      <p className="text-xs text-slate-400 font-medium">No notifications yet.</p>
                      <p className="text-xs text-slate-500 mt-1">Completed jobs will appear here.</p>
                    </div>
                  ) : (
                    notifications.map(n => (
                      <div key={n.id} className={`flex items-start gap-3 px-4 py-3.5 transition-colors hover:bg-slate-50 ${!n.read ? 'bg-brand-50/40' : ''}`}>
                        <div className="mt-0.5 shrink-0">
                          {n.type === 'success' && <CheckCircleIcon className="w-4 h-4 text-emerald-500" />}
                          {n.type === 'error' && <ExclamationCircleIcon className="w-4 h-4 text-red-500" />}
                          {n.type === 'export' && <ArrowDownTrayIcon className="w-4 h-4 text-brand-500" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-[13px] font-bold text-slate-900 truncate">{n.title}</p>
                          <p className="text-[12px] text-slate-500 mt-0.5 leading-snug">{n.body}</p>
                          {n.type === 'export' && n.taskId && (
                            <button
                              onClick={() => handleExportDownload(n.taskId!)}
                              className="mt-1.5 flex items-center gap-1 text-[11px] font-black uppercase tracking-wider text-brand-600 hover:text-brand-800 transition-colors"
                            >
                              <ArrowDownTrayIcon className="w-3 h-3" /> Download CSV
                            </button>
                          )}
                          <p className="text-[10px] text-slate-500 font-medium mt-1">{timeAgo(n.timestamp)}</p>
                        </div>
                        {!n.read && <div className="w-2 h-2 bg-brand-500 rounded-full mt-1.5 shrink-0" />}
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="h-6 w-px bg-slate-100 hidden sm:block" />

          {/* User menu */}
          <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild>
              <button className="flex items-center gap-2.5 outline-none group">
                <div className="w-8 h-8 rounded-full bg-brand-100 flex items-center justify-center border-2 border-white shadow-sm">
                  <span className="text-brand-700 font-bold text-xs">{getInitials(user?.name)}</span>
                </div>
                <span className="hidden sm:block text-[14px] font-semibold text-slate-700 group-hover:text-slate-900 transition-colors">
                  {user?.name || 'User'}
                </span>
              </button>
            </DropdownMenu.Trigger>

            <DropdownMenu.Portal>
              <DropdownMenu.Content
                align="end"
                sideOffset={10}
                className="z-50 min-w-[200px] bg-white rounded-2xl shadow-xl border border-slate-100 p-2"
              >
                <div className="px-3 py-2 border-b border-slate-50 mb-1">
                  <p className="text-sm font-bold text-slate-900 truncate">{user?.name}</p>
                  <p className="text-xs text-slate-400 truncate mt-0.5">{user?.email}</p>
                </div>
                <DropdownMenu.Item asChild>
                  <Link
                    to="/dashboard/my-profile"
                    className="flex items-center gap-2.5 px-3 py-2.5 text-sm font-bold text-slate-600 hover:bg-slate-100 hover:text-slate-900 rounded-xl cursor-pointer outline-none transition-colors"
                  >
                    <UserIcon className="w-4 h-4" /> Profile & Settings
                  </Link>
                </DropdownMenu.Item>
                {/* <DropdownMenu.Item asChild>
                  <a
                    href="https://localcontact.nexilo.in/contact"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2.5 px-3 py-2.5 text-sm font-bold text-brand-600 hover:bg-brand-50 rounded-xl cursor-pointer outline-none transition-colors"
                  >
                    <QuestionMarkCircleIcon className="w-4 h-4" /> Support & Help
                  </a>
                </DropdownMenu.Item> */}
                <DropdownMenu.Item
                  onClick={() => logout()}
                  className="flex items-center gap-2.5 px-3 py-2.5 text-sm font-bold text-red-600 hover:bg-red-50 rounded-xl cursor-pointer outline-none transition-colors"
                >
                  <ArrowRightOnRectangleIcon className="w-4 h-4" /> Sign out
                </DropdownMenu.Item>
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>
          </div>{/* end right-side group */}
        </header>

        <main className="flex-1 overflow-y-auto">
          <Outlet />
        </main>
      </div>

      <PlansModal
        isOpen={isPlansOpen}
        onClose={() => setIsPlansOpen(false)}
        onPurchaseSuccess={(n) => { if (user) setUser({ ...user, credits: (user.credits || 0) + n }); }}
      />
    </div>
  );
}
